import { css, cx } from '@linaria/core'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import type { ErrorResponse } from 'react-router-dom'
import { Button } from '#components/Button'
import { fragments, style } from '#styles'
import { useHomeNavigation } from '#utils/useHomeRouting'

const fourOhFourErrorClass = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 10px;
  min-width: 100%;
  min-height: 100%;
  color: ${style.themeColors.textDefault};

  // this is primarily to hide the crimes of low resolution background images

  &::before {
    z-index: -1;
    position: absolute;
    content: '';
    width: 100%;
    height: calc(100% - var(--header-height));
    background: url('/images/bg-404.jpg') center center;
    background-size: cover;
    filter: blur(2px);
  }

  > .status {
    font-size: 120px;
    line-height: 100%;
  }

  > .text {
    ${fragments.textStyle.heading.xl.subtle}
  }

  > .action {
    padding: 14px 16px;
    ${fragments.textStyle.body.l.bold}
  }

  > .row {
    display: flex;
    flex-direction: row;
    align-content: flex-start;
    align-items: center;

    flex-wrap: wrap;
  }

  > pre {
    padding: 10px;
    ${fragments.textStyle.body.l.regular}
    font-family: monospace;
    color: ${style.themeColors.textDimmed};
    background-color: ${style.themeColors.backgroundBoxed};
    max-height: 300px;
    user-select: text;
  }

  > h4 {
    ${fragments.textStyle.heading.l.subtle}
    margin-bottom: 4px;
    margin-top: 16px;
  }

  > * {
    ${fragments.textStyle.heading.xl.regular}
    color: ${style.themeColors.textDisabled};
  }
`

const errorFragments: Partial<Record<number, FC>> = {
  404: function NotFoundView() {
    const navigate = useHomeNavigation()

    const { t } = useTranslation('shared')
    return (
      <div className={cx('has-full-width-underlay', fourOhFourErrorClass)}>
        <div className="status">404</div>
        <div className="text">{t('error-handler.404-message', 'Unfortunately the page you are looking for does not exist.')}</div>
        <Button
          className="action"
          onClick={() => {
            navigate()
          }}
        >
          {t('common.button.back-to-home', 'Back to Home')}
        </Button>
      </div>
    )
  },
}

const DefaultErrorFragment: FC<{ error: ErrorResponse }> = ({ error }) => (
  <div className={fourOhFourErrorClass}>
    <div className="row">
      <div className="status">{error.status}</div>
      <div>{error.statusText}</div>
    </div>
    {error.data ? <pre>{JSON.stringify(error.data)}</pre> : null}
  </div>
)

export const RoutingError: FC<{ error: ErrorResponse }> = ({ error }) => {
  return errorFragments[error.status]?.({}) ?? <DefaultErrorFragment error={error} />
}
